// Layer 4 (UI) — "Month 4 to close" (locked comp `planning.html` A): the
// central close as the panel's primary figure with its comparison, the rate
// needed to close beside it, a bar of actual and forecast remainder against the
// month's budget tick, and the three scenario closes as hairline rows. A row
// press selects the scenario the chart emphasises (Central by default).
// Renders MonthClose.transform.ts; derives nothing.

import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { DowntimeCard } from '../downtime/DowntimeCard';
import type { MonthCloseRead } from './MonthClose.transform';
import { formatDst } from './PlanningBand.transform';
import type { PlanningScenarioName } from './Scene.transform';

export function MonthClose({
  read,
  selected,
  onSelect,
  className,
}: {
  read: MonthCloseRead;
  selected: PlanningScenarioName;
  onSelect: (name: PlanningScenarioName) => void;
  className?: string;
}) {
  return (
    <DowntimeCard
      title={read.title}
      aria-label={read.title}
      actions={<ActorChip actor="Engine">AI suggestion</ActorChip>}
      className={cn('flex flex-col', className)}
    >
      <div data-month-close className="grid grid-cols-[1fr_auto] items-end gap-x-3.5 tabular-nums">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">Central close</p>
          <p data-month-close-figure className="whitespace-nowrap text-2xl font-semibold leading-none tracking-[-0.01em] text-ink-primary">
            {formatDst(read.centralCloseSt)}
            <span className="ml-1 text-sm font-semibold text-muted">dst</span>
          </p>
        </div>
        <div className="border-l border-[var(--color-divider)] pl-3.5 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">Rate to close</p>
          <p data-month-close-rate className="whitespace-nowrap text-lg font-semibold leading-tight text-ink-primary">
            {read.stphNeededToClose.toFixed(1)}
            <span className="ml-1 text-xs font-semibold text-muted">stph</span>
          </p>
        </div>
        <p className="truncate text-xs text-muted">
          <span className="font-semibold text-ink-secondary">{read.centralPctOfBudget.toFixed(1)}%</span> of{' '}
          <span className="font-semibold text-ink-secondary">{formatDst(read.monthBudgetSt)} dst</span> budget
        </p>
        <p className="whitespace-nowrap pl-3.5 text-right text-xs text-muted">
          vs <span className="font-semibold text-ink-secondary">{read.runningStph.toFixed(1)}</span> running
        </p>
      </div>

      <div className="mb-1 mt-3.5 tabular-nums">
        <div className="relative h-2.5 rounded bg-[var(--hairline)]" aria-hidden="true">
          <span className="absolute inset-y-0 left-0 rounded-l bg-ink-primary" style={{ width: `${read.actualPct}%` }} />
          <span
            className="absolute inset-y-0"
            style={{
              left: `${read.actualPct}%`,
              width: `${read.centralRemainderPct}%`,
              background: 'repeating-linear-gradient(135deg, var(--ink-secondary) 0 2px, transparent 2px 5px)',
            }}
          />
          <span className="absolute -bottom-1 -top-1 w-0.5 bg-ink-primary" style={{ left: `${read.budgetPct}%` }} />
        </div>
        <div className="relative mt-1.5 h-4 text-[11px] text-muted">
          <span className="absolute left-0 whitespace-nowrap">
            <span className="font-semibold text-ink-secondary">{formatDst(read.actualMtdSt)}</span> actual · {read.windowLabel}
          </span>
          <span className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${read.budgetPct}%` }}>budget</span>
        </div>
      </div>

      <div className="mt-1.5 flex flex-col" role="group" aria-label="Month-close scenarios">
        {read.scenarios.map((scenario) => {
          const on = scenario.name === selected;
          return (
            <button
              key={scenario.name}
              type="button"
              data-close-scenario={scenario.name}
              aria-pressed={on}
              title={scenario.provenance}
              onClick={() => onSelect(scenario.name)}
              className={cn(
                '-mx-2 grid grid-cols-[1fr_auto] items-baseline gap-x-3 rounded-lg border-b border-[var(--color-divider)] px-2 py-1.5 text-left transition-colors duration-150 last:border-b-0 hover:bg-[var(--surface-1)] motion-reduce:transition-none',
                on ? 'bg-[var(--surface-1)]' : '',
              )}
            >
              <span className="min-w-0 text-[13px] font-medium text-ink-primary">
                {on ? <span aria-hidden="true" className="mr-1.5 inline-block size-1.5 rounded-full bg-ink-primary align-[2px]" /> : null}
                {scenario.name}
                <span className="block truncate text-xs font-normal text-muted">
                  {scenario.repeatsPrimary ? scenario.basis : `${scenario.rateStph.toFixed(1)} stph · ${scenario.basis}`}
                </span>
              </span>
              {/* The Central close is the panel's primary figure, so its row
                  restates neither that figure nor the running rate. */}
              {scenario.repeatsPrimary ? null : (
                <span className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-ink-primary">{formatDst(scenario.closeSt)} dst</span>
              )}
            </button>
          );
        })}
      </div>
      <p data-month-close-formula className="mt-auto pt-2 text-xs tabular-nums text-muted">{read.formula}</p>
    </DowntimeCard>
  );
}
